import React, {
  useState, useEffect, useCallback, useRef, useMemo
} from 'react';
import { PAGE_SIZE } from '../productListHelper';

export const useObserverLoadQueue = ({
  groups = [],
  loadItemsOnDemand,
  productsDisplayed,
  listId
}) => {
  const [loadingItemIds, setLoadingItemIds] = useState(new Set());

  // Observer stack system for sequential placeholder loading
  const [observerStack, setObserverStack] = useState([]);
  const [isProcessingObserverStack, setIsProcessingObserverStack] = useState(false);
  const observerStackRef = useRef([]);
  const triggeredBatchesRef = useRef(new Map());
  const previousListIdRef = useRef(null);

  // Compute notLoadedItems per group (similar to useItemGroups logic)
  const groupNotLoadedMap = useMemo(() => {
    const __loadedItemIds = new Set(
      productsDisplayed?.map((product) => product?.itemId || product?.intentId).filter(Boolean) || []
    );

    const __map = {};
    groups.forEach((group) => {
      const groupKey = group.groupId || '__ungrouped__';
      const notLoaded = (group.items || [])
        .map((item) => item.itemId || item.intentId)
        .filter((id) => id && !__loadedItemIds.has(id));
      __map[groupKey] = notLoaded;
    });
    return __map;
  }, [groups, productsDisplayed]);

  // Reset triggered batches when list changes
  useEffect(() => {
    if (listId && listId !== previousListIdRef.current) {
      previousListIdRef.current = listId;
      triggeredBatchesRef.current.clear();
    }
  }, [listId]);

  // Observer stack processing - sequential loading when placeholders enter viewport
  useEffect(() => {
    const processObserverStack = async () => {
      if (isProcessingObserverStack || observerStack.length === 0) {
        return;
      }

      setIsProcessingObserverStack(true);

      try {
        const nextTrigger = observerStack[0];
        const { groupId } = nextTrigger;

        const notLoadedItems = groupNotLoadedMap[groupId] || [];

        if (notLoadedItems.length > 0) {
          const itemsToLoad = notLoadedItems.slice(0, PAGE_SIZE);

          setLoadingItemIds((prev) => {
            const newSet = new Set(prev);
            itemsToLoad.forEach((itemId) => newSet.add(itemId));
            return newSet;
          });

          try {
            await loadItemsOnDemand(itemsToLoad);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error loading items from observer:', error);
            setLoadingItemIds((prev) => {
              const newSet = new Set(prev);
              itemsToLoad.forEach((itemId) => newSet.delete(itemId));
              return newSet;
            });
          }
        }

        // Remove the processed trigger from stack
        setObserverStack((prev) => prev.slice(1));
      } finally {
        setIsProcessingObserverStack(false);
      }
    };

    processObserverStack();
  }, [observerStack, isProcessingObserverStack, groupNotLoadedMap, loadItemsOnDemand]);

  // Check if placeholders are still in viewport after loading completes
  useEffect(() => {
    // Only run check after loading completes (when loadingItemIds becomes empty)
    if (loadingItemIds.size === 0 && !isProcessingObserverStack) {
      // Small delay to allow DOM to update
      const timeoutId = setTimeout(() => {
        // Check if any placeholder observers are currently visible
        const placeholderObservers = document.querySelectorAll('[data-testid^="placeholder-observer-"]');

        placeholderObservers.forEach((element) => {
          const rect = element.getBoundingClientRect();
          const isInViewport = (
            rect.top >= 0
            && rect.left >= 0
            && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + 300
            && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
          );

          if (isInViewport) {
            // Extract groupId from testid
            const testId = element.getAttribute('data-testid');
            const match = testId.match(/placeholder-observer-(.+)-\d+/);
            if (match) {
              const groupId = match[1];
              const notLoadedItems = groupNotLoadedMap[groupId] || [];

              // Only trigger if group still has items to load and not already in stack
              if (notLoadedItems.length > 0) {
                const alreadyInStack = observerStackRef.current.some((trigger) => trigger.groupId === groupId);
                if (!alreadyInStack) {
                  setObserverStack((prev) => [...prev, { groupId, placeholderIndex: 0 }]);
                  observerStackRef.current = [...observerStackRef.current, { groupId, placeholderIndex: 0 }];
                }
              }
            }
          }
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [loadingItemIds, isProcessingObserverStack, groupNotLoadedMap]);

  // Sync observerStackRef with observerStack state
  useEffect(() => {
    observerStackRef.current = observerStack;
  }, [observerStack]);

  // Clean up loadingItemIds once items appear in productsDisplayed
  // Also handles failed items that never load (404s, errors, etc.)
  useEffect(() => {
    if (loadingItemIds.size === 0) return undefined;

    const loadedItemIdsSet = new Set(
      productsDisplayed?.map((product) => product?.itemId || product?.intentId).filter(Boolean) || []
    );

    // Immediately remove items that have loaded
    setLoadingItemIds((prev) => {
      const newSet = new Set(prev);
      let hasChanges = false;

      prev.forEach((itemId) => {
        if (loadedItemIdsSet.has(itemId)) {
          newSet.delete(itemId);
          hasChanges = true;
        }
      });

      return hasChanges ? newSet : prev;
    });

    // Set a timeout to clean up items that fail to load (404s, errors)
    // After 5 seconds, assume any remaining loading items have failed
    const timeoutId = setTimeout(() => {
      setLoadingItemIds((prev) => {
        if (prev.size === 0) return prev;

        const currentLoadedSet = new Set(
          productsDisplayed?.map((product) => product?.itemId || product?.intentId).filter(Boolean) || []
        );

        const newSet = new Set(prev);
        let hasChanges = false;

        prev.forEach((itemId) => {
          // If item still not in productsDisplayed after timeout, it failed
          if (!currentLoadedSet.has(itemId)) {
            newSet.delete(itemId);
            hasChanges = true;
            // eslint-disable-next-line no-console
            console.warn(`Item ${itemId} failed to load (timeout or error)`);
          }
        });

        return hasChanges ? newSet : prev;
      });
    }, 5000); // 5 second timeout for failed items

    return () => clearTimeout(timeoutId);
  }, [productsDisplayed, loadingItemIds]);

  // Callback when placeholder observer enters viewport
  const handleObserverIntersect = useCallback(({ groupId, observerIndex, batchNumber }) => {
    const batchKey = `${groupId}-${batchNumber}`;
    if (triggeredBatchesRef.current.has(batchKey)) {
      return;
    }
    triggeredBatchesRef.current.set(batchKey, true);

    setObserverStack((prev) => {
      const alreadyInStack = prev.some((trigger) => (
        trigger.groupId === groupId && trigger.batchNumber === batchNumber
      ));
      if (alreadyInStack) {
        return prev;
      }
      return [...prev, { groupId, observerIndex, batchNumber }];
    });
  }, []);

  return {
    loadingItemIds,
    setLoadingItemIds,
    handleObserverIntersect,
    isProcessing: isProcessingObserverStack
  };
};
